import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export const ACCENTS = {
  dayrit : { name: 'Dayrit',  a: '#4A90E2', b: '#36C9BE', c: '#3EE07A' },
  ocean  : { name: 'Ocean',   a: '#2563EB', b: '#0891B2', c: '#06B6D4' },
  sunset : { name: 'Sunset',  a: '#F97316', b: '#EF4444', c: '#EC4899' },
  forest : { name: 'Forest',  a: '#16A34A', b: '#059669', c: '#0D9488' },
  purple : { name: 'Purple',  a: '#7C3AED', b: '#9333EA', c: '#C026D3' },
  amber  : { name: 'Amber',   a: '#D97706', b: '#CA8A04', c: '#65A30D' },
};

function makeGrad(a, b, c) {
  return `linear-gradient(135deg, ${a} 0%, ${b} 50%, ${c} 100%)`;
}

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? `${parseInt(result[1],16)},${parseInt(result[2],16)},${parseInt(result[3],16)}` : '54,201,190';
}

function applyTheme(dark, accentKey) {
  const r   = document.documentElement;
  const acc = ACCENTS[accentKey] || ACCENTS.dayrit;
  const grad = makeGrad(acc.a, acc.b, acc.c);
  const rgb  = hexToRgb(acc.b);

  r.style.setProperty('--accent',       acc.b);
  r.style.setProperty('--accent-a',     acc.a);
  r.style.setProperty('--accent-b',     acc.b);
  r.style.setProperty('--accent-c',     acc.c);
  r.style.setProperty('--brand-grad',   grad);
  r.style.setProperty('--brand-shadow', `rgba(${rgb},0.28)`);
  r.style.setProperty('--accent-ring',  `rgba(${rgb},0.18)`);
  r.style.setProperty('--accent-light', `rgba(${rgb},0.10)`);

  if (dark) {
    r.style.setProperty('--bg',         '#161616');
    r.style.setProperty('--surface',    '#202020');
    r.style.setProperty('--surface2',   '#1a1a1a');
    r.style.setProperty('--input-bg',   '#252525');
    r.style.setProperty('--border',     '#2e2e2e');
    r.style.setProperty('--hover',      '#282828');
    r.style.setProperty('--divider',    '#242424');
    r.style.setProperty('--text',       '#e4e4e4');
    r.style.setProperty('--text2',      '#888888');
    r.style.setProperty('--text3',      '#4e4e4e');
    r.style.setProperty('--sidebar-bg', '#1a1a1a');
  } else {
    r.style.setProperty('--bg',         '#f7f7f5');
    r.style.setProperty('--surface',    '#ffffff');
    r.style.setProperty('--surface2',   '#fafaf8');
    r.style.setProperty('--input-bg',   '#fafaf8');
    r.style.setProperty('--border',     '#e8e8e4');
    r.style.setProperty('--hover',      '#f2f2ef');
    r.style.setProperty('--divider',    '#f0f0ec');
    r.style.setProperty('--text',       '#1a1a1a');
    r.style.setProperty('--text2',      '#666666');
    r.style.setProperty('--text3',      '#aaaaaa');
    r.style.setProperty('--sidebar-bg', '#fafaf8');
  }
}

export function ThemeProvider({ children }) {
  const [dark,   setDark]   = useState(() => localStorage.getItem('dayrit_dark') === 'true');
  const [accent, setAccent] = useState(() => localStorage.getItem('dayrit_accent') || 'dayrit');

  useEffect(() => { applyTheme(dark, accent); }, [dark, accent]);

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    localStorage.setItem('dayrit_dark', String(next));
  };

  const changeAccent = (key) => {
    setAccent(key);
    localStorage.setItem('dayrit_accent', key);
  };

  return (
    <ThemeContext.Provider value={{ dark, accent, toggleDark, changeAccent, accentObj: ACCENTS[accent] || ACCENTS.dayrit }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);